import { useState } from "react";
import { Copy, Receipt } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLoad, act } from "@/lib/hooks";
import { api, fmt } from "@/lib/api";
import { Section, Field, NativeSelect } from "@/components/common";

export function IssueInvoice({ tenantId, onIssued }) {
  const [d] = useLoad("/platform/plans");
  const [kind, setKind] = useState("plan");
  const [code, setCode] = useState("");
  const [interval, setInterval] = useState("monthly");
  const [out, setOut] = useState(null);
  if (!d) return null;
  const opts = kind === "plan"
    ? d.plans.filter((p) => p.visibility !== "internal").map((p) => ({ value: p.code, label: `${p.name} · ${fmt(p.monthly_price, "USD")}/mo` }))
    : d.addons.filter((a) => (a.bundle || a.capability) && !a.price_placeholder).map((a) => ({ value: a.code, label: `${a.name} · ${fmt(a.monthly_price, "USD")}/mo` }));
  const issue = async () => {
    const body = { [kind === "plan" ? "plan_code" : "addon_code"]: code, interval, callback_url: `${window.location.origin}/app/billing/callback` };
    const r = await act(() => api.post(`/tenants/${tenantId}/issue-invoice`, body), "Invoice issued");
    if (r) { setOut(r); onIssued(); }
  };
  const copy = () => navigator.clipboard.writeText(out.authorization_url).then(() => toast.success("Payment link copied"), () => toast.error("Copy failed"));
  return (
    <Section title="Issue invoice">
      <div className="grid gap-3 p-5 sm:grid-cols-4">
        <Field label="Bill for"><NativeSelect testId="issue-kind-select" value={kind} onChange={(v) => { setKind(v); setCode(""); }} options={[{ value: "plan", label: "Plan" }, { value: "addon", label: "Add-on" }]} /></Field>
        <Field label={kind === "plan" ? "Plan" : "Add-on"}><NativeSelect testId="issue-code-select" value={code} onChange={setCode} options={[{ value: "", label: "Choose…" }, ...opts]} /></Field>
        <Field label="Interval"><NativeSelect testId="issue-interval-select" value={interval} onChange={setInterval} options={[{ value: "monthly", label: "Monthly" }, { value: "annual", label: "Annual" }]} /></Field>
        <div className="flex items-end"><Button data-testid="issue-invoice-button" disabled={!code} onClick={issue} className="w-full bg-[#13294B] hover:bg-[#1c3a66]"><Receipt className="mr-1 h-4 w-4" />Issue invoice</Button></div>
        {out && <div className="sm:col-span-4" data-testid="issue-invoice-result">
          <p className="mb-1 text-xs text-slate-500">{out.invoice.number} · {fmt(out.invoice.amount, "USD")} (charged as GHS {out.invoice.charge_amount ?? ""}). Send this Paystack link to the tenant:</p>
          <div className="flex gap-2"><Input readOnly value={out.authorization_url} data-testid="issue-invoice-link" /><Button variant="outline" data-testid="issue-invoice-copy" onClick={copy}><Copy className="mr-1 h-4 w-4" />Copy</Button></div>
        </div>}
      </div>
    </Section>
  );
}
